import classNames from "classnames";
import { Field, useField } from "formik";
import { omit } from "lodash";
import { useState } from "react";

import { InsurancePlan } from "pwapi";

import useLocationInputs from "shared/hooks/useLocationInputs";

import { AccessionSelect, Input } from "ui/accessioning/AccessionForm";
import { InsurancePlanOption } from "ui/CaseFormInputs/SelectSearchOptions";
import useInsuranceInputs from "ui/CaseFormInputs/useInsuranceInputs";
import useUserInformationInputs from "ui/CaseFormInputs/useUserInformationInputs";

import FormHeaders from "./FormHeaders";
import InsurancePlanLookupDialog from "./InsurancePlanLookupDialog";

const InsuranceForm = ({
  i,
  insurance,
  primary = false,
  onBlur,
  usePrefix,
  useInsurancePlan = false,
  adminLabId,
  isAdmin,
  onHandleDelete,
  displaySubmit,
  onHandleDiscardChanges,
  onHandleChangeRelationship,
  onHandleChangeInsurancePlan,
  dirty,
  errors = {},
  touched = {},
  warnings = {},
}: {
  i: number;
  insurance: any;
  primary?: boolean;
  onBlur?: Function;
  usePrefix?: boolean;
  useInsurancePlan?: boolean;
  adminLabId?: number;
  isAdmin?: boolean;
  displaySubmit?: boolean;
  dirty?: boolean;
  onHandleDiscardChanges?: Function;
  onHandleChangeRelationship?: Function;
  onHandleChangeInsurancePlan?: Function;
  onHandleDelete?: Function;
  errors?: any;
  touched?: any;
  warnings?: any;
}) => {
  const insuranceInputs = useInsuranceInputs({
    parentResource: usePrefix ? `insurance[${i}]` : undefined,
    isAdmin,
  });

  const userInfoInputs = useUserInformationInputs(
    `insurance[${i}]`,
    true,
    usePrefix ? `insurance[${i}]` : undefined,
    "insured"
  );

  const locationInputs = useLocationInputs(
    `insurance[${i}]`,
    usePrefix ? `insurance[${i}]` : undefined,
    "insurance_company",
    "Insurance Company"
  );

  const disableInputs = insurance.relationship_to_patient === "SELF";

  const [showAdvancedLookup, setShowAdvancedLookup] = useState<boolean>(false);

  // Meta is an unused variable
  // eslint-disable-next-line
  const [field, meta, { setValue, setTouched }] = useField(
    insuranceInputs.insurancePlan.name
  );

  return (
    <>
      <FormHeaders
        title={`${primary ? "Primary" : "Additional"} Insurance`}
        displaySubmit={displaySubmit}
        dirty={dirty}
        onHandleDiscardChanges={onHandleDiscardChanges}
        onHandleDelete={!primary && onHandleDelete ? onHandleDelete : undefined}
      />
      {showAdvancedLookup && onHandleChangeInsurancePlan && (
        <div>
          <InsurancePlanLookupDialog
            apiService={insuranceInputs.insurancePlan.serviceFunction}
            onClose={() => setShowAdvancedLookup(false)}
            selectedInsurance={insurance.plan}
            onSelect={(insurance: InsurancePlan) => {
              onHandleChangeInsurancePlan(insurance);
              setValue(insurance);
              setShowAdvancedLookup(false);
            }}
          />
        </div>
      )}

      <div className="LISForm__section">
        <div className="Accession__input Input--1">
          <label>Sub Relationship</label>
          <Field
            {...omit(userInfoInputs.subRelationship, "defaultOptionLabel")}
            as="select"
            onBlur={onBlur}
            onChange={(e: any) => {
              onHandleChangeRelationship &&
                onHandleChangeRelationship(e.target.value);
            }}
          >
            <option value="">
              {userInfoInputs.subRelationship.defaultOptionLabel}
            </option>
            {userInfoInputs.subRelationship.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
        </div>
        <Input
          {...userInfoInputs.lastName}
          className={classNames("Input--2", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={onBlur}
          disabled={disableInputs}
        />
        <Input
          {...userInfoInputs.firstName}
          className={classNames("Input--2", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={onBlur}
          disabled={disableInputs}
        />
        <Input
          {...userInfoInputs.dateOfBirth}
          className={classNames("Input--1", {
            "Accession__input--disabled": disableInputs,
          })}
          handleOnBlur={onBlur}
          error={
            errors.insured_date_of_birth ||
            errors?.insurance?.[i]?.insured_date_of_birth
          }
          touched={
            touched.insured_date_of_birth ||
            touched?.insurance?.[i]?.insured_date_of_birth
          }
          warning={
            warnings.insured_date_of_birth ||
            warnings[`insurance[${i}]`]?.insured_date_of_birth
          }
          disabled={disableInputs}
        />
        {i === 0 && (
          <Input
            {...insuranceInputs.hasAbn}
            className="Input--1"
            checked={insurance.has_abn}
            handleOnBlur={onBlur}
          />
        )}
        <Input
          {...insuranceInputs.preapprovalID}
          className={i === 0 ? "Input--1" : "Input--2"}
          handleOnBlur={onBlur}
        />
        <Input
          {...insuranceInputs.groupEmployerId}
          className="Input--2"
          handleOnBlur={onBlur}
        />
        <Input
          {...insuranceInputs.policyNumber}
          className="Input--2"
          handleOnBlur={onBlur}
        />

        {isAdmin && !adminLabId && useInsurancePlan ? (
          <div className="Accession__input Input--6" style={{ padding: `1em` }}>
            The case is missing lab information and cannot assign insurance
            company details. Please contact support for assistance.
          </div>
        ) : useInsurancePlan ? (
          <div className="Accession__input Input--6">
            <label>{insuranceInputs.insurancePlan.label}</label>
            <AccessionSelect
              {...insuranceInputs.insurancePlan}
              handleOnBlur={
                onBlur &&
                ((newValue: InsurancePlan) => {
                  onHandleChangeInsurancePlan &&
                    onHandleChangeInsurancePlan(newValue);
                })
              }
            >
              {insurance.plan ? (
                <InsurancePlanOption object={insurance.plan} />
              ) : (
                "Select an insurance plan..."
              )}
            </AccessionSelect>
            <button
              style={{ alignSelf: "flex-end" }}
              type="button"
              className="Button Button--purple"
              onClick={() => setShowAdvancedLookup(true)}
            >
              Advanced Look Up
            </button>
          </div>
        ) : (
          <>
            <Input
              {...insuranceInputs.insuranceCompanyName}
              className="Input--6"
              handleOnBlur={onBlur}
            />
            <Input
              {...locationInputs.address}
              className="Input--6"
              handleOnBlur={onBlur}
            />
            <Input
              {...locationInputs.address2}
              className="Input--6"
              handleOnBlur={onBlur}
            />

            <Input
              {...locationInputs.city}
              className="Input--2"
              handleOnBlur={onBlur}
            />

            <div className="Accession__input Input--2">
              <label>{locationInputs.state.label}</label>
              <Field
                {...omit(locationInputs.state, "defaultOptionLabel")}
                as="select"
                onBlur={onBlur}
              >
                <option value="">
                  {locationInputs.state.defaultOptionLabel}
                </option>
                {locationInputs.state.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
            </div>

            <Input
              {...locationInputs.zipcode}
              className="Input--2"
              handleOnBlur={onBlur}
            />
          </>
        )}
      </div>
    </>
  );
};

export default InsuranceForm;
